import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPhone, FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

const PhoneLogin = () => {
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [countdown, setCountdown] = useState(0);
  
  const { sendOTP, verifyOTP } = useAuth();
  const navigate = useNavigate();
  const recaptchaContainerRef = useRef(null);

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Ensure reCAPTCHA container is properly initialized
  useEffect(() => {
    if (step === 'phone') {
      // Wait for DOM to be ready
      const timer = setTimeout(() => {
        const container = document.getElementById('recaptcha-container');
        if (container) {
          // Ensure container is visible and properly styled
          container.style.display = 'block';
          container.style.visibility = 'visible';
          container.style.position = 'relative';
          container.style.minHeight = '78px'; // Minimum height for reCAPTCHA
          container.style.width = '100%';
          container.style.margin = '15px 0';
          
          // Clear any existing content
          container.innerHTML = '';
        } else {
          console.error('reCAPTCHA container not found');
        }
      }, 1000); // Wait 1 second for DOM to be ready
      
      return () => clearTimeout(timer);
    }
  }, [step]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate phone number format
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    // Format phone number to international format
    let formattedPhone = phoneNumber;
    if (!phoneNumber.startsWith('+')) {
      formattedPhone = `+91${phoneNumber}`; // Default to India (+91)
    }

    try {
      const result = await sendOTP(formattedPhone, 'recaptcha-container');
      if (result.success) {
        setConfirmationResult(result.confirmationResult);
        setStep('otp');
        setCountdown(30); // 30 seconds countdown
        setSuccess('OTP sent successfully!');
      } else {
        setError(result.error);
        // If reCAPTCHA failed, allow retry
        if (result.error.includes('reCAPTCHA')) {
          setError(result.error + ' Click "Send OTP" again after completing reCAPTCHA.');
        }
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await verifyOTP(confirmationResult, otp);
      if (result.success) {
        setSuccess('Phone verification successful! Redirecting...');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    let formattedPhone = phoneNumber;
    if (!phoneNumber.startsWith('+')) {
      formattedPhone = `+91${phoneNumber}`;
    }

    try {
      const result = await sendOTP(formattedPhone, 'recaptcha-container');
      if (result.success) {
        setConfirmationResult(result.confirmationResult);
        setCountdown(30);
        setSuccess('OTP resent successfully!');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhone = () => {
    setStep('phone');
    setOtp('');
    setError('');
    setSuccess('');
    setCountdown(0);
  };

  return (
    <div className="auth-page">
      <div className="container">
        <motion.div
          className="auth-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="auth-header">
            <Link to="/" className="logo">
              <div className="logo-v">V</div>
              <div className="logo-text">
                <span className="brand-name">Vastré</span>
                <span className="brand-tagline">BY VIJH</span>
              </div>
            </Link>
            <h1>{step === 'phone' ? 'Phone Login' : 'Verify OTP'}</h1>
            <p>{step === 'phone' ? 'Enter your phone number to continue' : 'Enter the 6-digit code sent to your phone'}</p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handleSendOTP} className="auth-form">
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <div className="input-with-icon">
                  <FaPhone className="input-icon" />
                  <input
                    type="tel"
                    id="phone"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
                <small className="form-help">Enter with country code (e.g., +91 for India)</small>
              </div>

              {/* reCAPTCHA Container */}
              <div 
                id="recaptcha-container" 
                ref={recaptchaContainerRef} 
                style={{ 
                  margin: '15px 0',
                  minHeight: '78px',
                  display: 'block',
                  visibility: 'visible',
                  position: 'relative',
                  width: '100%',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#f9f9f9'
                }}
              >
                <div style={{ textAlign: 'center', color: '#666' }}>
                  Loading reCAPTCHA...
                </div>
              </div>
              
              {/* reCAPTCHA Instructions */}
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#e3f2fd', 
                border: '1px solid #2196f3', 
                borderRadius: '4px', 
                margin: '10px 0',
                fontSize: '14px'
              }}>
                <strong>reCAPTCHA Instructions:</strong>
                <br />
                1. Complete the reCAPTCHA verification above
                <br />
                2. Click "Send OTP" after completing reCAPTCHA
                <br />
                3. If reCAPTCHA doesn't appear, refresh the page
              </div>

              {/* Error and Success Messages */}
              {error && (
                <div className="auth-message error">
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="auth-message success">
                  <span>{success}</span>
                </div>
              )}

              <button type="submit" className="btn-primary auth-btn" disabled={loading}>
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>

              <div className="auth-footer">
                <p>Or <Link to="/login">login with email</Link></p>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="auth-form">
              <div className="form-group">
                <label htmlFor="otp">OTP Code</label>
                <div className="input-with-icon">
                  <FaPhone className="input-icon" />
                  <input
                    type={showOtp ? 'text' : 'password'}
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowOtp(!showOtp)}
                  >
                    {showOtp ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <small className="form-help">Enter the 6-digit code sent to {phoneNumber}</small>
              </div>

              {/* Error and Success Messages */}
              {error && (
                <div className="auth-message error">
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="auth-message success">
                  <span>{success}</span>
                </div>
              )}

              <button type="submit" className="btn-primary auth-btn" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="otp-actions">
                <button
                  type="button"
                  className="btn-secondary resend-btn"
                  onClick={handleResendOTP}
                  disabled={countdown > 0 || loading}
                >
                  {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                </button>
                
                <button
                  type="button"
                  className="btn-secondary back-btn"
                  onClick={goBackToPhone}
                  disabled={loading}
                >
                  <FaArrowLeft /> Back
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PhoneLogin;
